import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useConfig } from "../context/ConfigContext";
import { toast } from "sonner";
import { Upload, Star, Trash2, Loader2 } from "lucide-react";

const CATEGORIES = [
  "Inforno logos", "Passport Experience logos", "Thailand campaign graphics", "Bangkok", "Phuket",
  "Bangkok Marriott", "Grand Mercure", "Chao Phraya", "Phi Phi", "Elephant Sanctuary", "YONA",
  "Thai Cooking", "Jamiel/Michelle", "Real travelers", "Past trip photos", "Videos", "Flyers", "Covers", "Brand art", "Uncategorized",
];

function uploadErrorMessage(err, file) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return `${file.name}: ${detail}`;
  if (status === 413) return `${file.name}: file is too large.`;
  if (status === 415) return `${file.name}: unsupported file type.`;
  if (status === 401) return "Your session expired — please sign in again.";
  if (!err?.response || err?.code === "ECONNABORTED") return `${file.name}: upload timed out or lost connection. Please try again.`;
  return `${file.name}: upload failed (${status}). Please try again.`;
}

export default function MediaLibrary() {
  const [media, setMedia] = useState([]);
  const [filter, setFilter] = useState("All");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uCat, setUCat] = useState("Uncategorized");
  const [uTags, setUTags] = useState("");

  const load = () => api.media().then(setMedia).catch(() => {});
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setProgress(0);
    let ok = 0;
    try {
      for (const f of files) {
        try {
          await api.uploadFileChunked(f, { category: uCat, tags: uTags }, setProgress);
          ok++;
        } catch (err) {
          toast.error(uploadErrorMessage(err, f));
        }
      }
      if (ok) { toast.success(`Uploaded ${ok} file(s)`); load(); }
    } finally { setUploading(false); setProgress(0); e.target.value = ""; }
  };

  const cats = ["All", ...Array.from(new Set(media.map((m) => m.category)))];
  const shown = filter === "All" ? media : media.filter((m) => m.category === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-[#D4AF37]">Upload once · use many times</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Media Library</h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <select value={uCat} onChange={(e) => setUCat(e.target.value)} data-testid="upload-category"
            className="px-3 py-2.5 rounded-lg bg-[#12151E] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input placeholder="tags, comma separated" value={uTags} onChange={(e) => setUTags(e.target.value)}
            className="px-3 py-2.5 rounded-lg bg-[#12151E] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37] w-44" />
          <label data-testid="upload-media-btn" className="gold-btn px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 cursor-pointer">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? (progress > 0 ? `Uploading ${progress}%` : "Uploading…") : "Upload"}
            <input type="file" multiple accept="image/*,video/*" onChange={onUpload} className="hidden" />
          </label>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {cats.map((c) => (
          <button key={c} onClick={() => setFilter(c)}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === c ? "border-[#D4AF37] text-[#F3E5AB] bg-[#222736]" : "border-[#2A3042] text-[#94A3B8]"}`}>{c}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="card-lux p-12 text-center text-[#64748B]">No media yet. Upload logos, hotel photos and Thailand imagery to reuse across every post.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {shown.map((m) => (
            <div key={m.id} className="card-lux overflow-hidden group" data-testid={`media-${m.id}`}>
              <div className="aspect-square bg-[#0A0C10] relative">
                {m.kind === "video"
                  ? <video src={m.url} className="w-full h-full object-cover" />
                  : <img src={m.url} alt={m.title} className="w-full h-full object-cover" />}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => api.updateMedia(m.id, { favorite: !m.favorite }).then(load)}
                    className="w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
                    <Star className={`w-3.5 h-3.5 ${m.favorite ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white"}`} />
                  </button>
                  <button data-testid={`del-media-${m.id}`} onClick={() => api.delMedia(m.id).then(load)}
                    className="w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              </div>
              <div className="p-2">
                <div className="text-xs text-[#F8FAFC] truncate">{m.title}</div>
                <div className="text-[10px] text-[#64748B]">{m.category}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
